import React, { useEffect, useMemo, useRef, useState } from "react";
import { parseISO } from "date-fns";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from "@exiftrator/shared/components/ui";
import {
  formatDateTimeShortcutInput,
  parseDateTimeShortcutInput,
} from "../../utils/dateTimeShortcutInput";

function getInitialInputValue(files, targetIndices) {
  const firstDatedFile = targetIndices
    .map((index) => files[index])
    .find((file) => file?.modifiedExifData?.dateTaken);

  if (firstDatedFile) {
    const parsed = parseISO(firstDatedFile.modifiedExifData.dateTaken);
    if (!Number.isNaN(parsed.getTime())) {
      return formatDateTimeShortcutInput(parsed);
    }
  }

  return formatDateTimeShortcutInput(new Date());
}

function DateTimeShortcutDialog({
  open,
  onOpenChange,
  files,
  targetIndices,
  onApplyDateTime,
}) {
  const inputRef = useRef(null);
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const targetCount = targetIndices.length;
  const targetLabel = useMemo(
    () => (targetCount === 1 ? "1 photo" : `${targetCount} photos`),
    [targetCount],
  );

  useEffect(() => {
    if (!open) return;

    setValue(getInitialInputValue(files, targetIndices));
    setError("");

    const focusTimer = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);

    return () => window.clearTimeout(focusTimer);
  }, [files, open, targetIndices]);

  const applyDateTime = () => {
    if (targetCount === 0) {
      onOpenChange(false);
      return;
    }

    const parsed = parseDateTimeShortcutInput(value);
    if (!parsed) {
      setError("Enter a valid date and time.");
      return;
    }

    onApplyDateTime(parsed);
    toast.success(`Date taken updated for ${targetLabel}.`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Set date taken</DialogTitle>
          <DialogDescription>
            Applies to {targetLabel}. Type a date/time, then press Enter.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-2">
          <Label htmlFor="shortcut-date-time">Date and time</Label>
          <Input
            ref={inputRef}
            id="shortcut-date-time"
            value={value}
            onChange={(event) => {
              setValue(event.target.value);
              setError("");
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                applyDateTime();
              }
            }}
            placeholder="2026-05-03 14:30"
          />
          {error && <p className="text-sm text-text-danger">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={applyDateTime}>Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DateTimeShortcutDialog;
